/**
 * k4 — <k4-rain>: deszcz znaków na stronie hasła (k4-gate).
 *
 * Canvas na całym ekranie za treścią. Kolumny spadających glifów; każda
 * klatka przyciemnia płótno półprzezroczystą czernią, więc znaki zostawiają
 * gasnący ślad. Paleta z tokenów motywu (czytane z :root): głowa kolumny
 * w --k4-fg, ślad w --k4-ink-muted, tło w --k4-bg — monochromatycznie,
 * bez zieleni, zgodnie z DESIGN.md.
 *
 * Trzy rodzaje kolumn (2026-10-13, życzenie właściciela: „K444 i znaczki
 * biżuteryjne, łańcuszki, pierścienie, na przemian"):
 * - „word": litery K-4-4-4 lecą po kolei, jedna pod drugą, z odstępem —
 *   znak marki czyta się w pionie;
 * - „chain": ogniwa i pierścienie z glifów geometrycznych (∞ ○ ◯ ⊙ ⊚ ◇ ⬡);
 * - „mix": cyfry i znaki techniczne, cztery częściej niż reszta.
 * Co jakiś czas w głowie kolumny zamiast glifu pojawia się sam znak K444
 * (data-k4-logo = PNG z assets, czarny na alfie, rysowany z invert).
 *
 * prefers-reduced-motion: jedna statyczna klatka, bez animacji.
 * Karta w tle: pętla stoi, po powrocie rusza dalej.
 */
const SETS = {
  word: 'K444 ',
  chain: '∞○◯⊙⊚◇⬡∞○◯',
  mix: '4444K0123456789//|<>[]+-=:.44',
};

class K4Rain extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas') || this.appendChild(document.createElement('canvas'));
    this.ctx = this.canvas.getContext('2d');
    this.fontPx = 16;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const root = getComputedStyle(document.documentElement);
    const token = (name, fallback) => (root.getPropertyValue(name) || fallback).trim();
    this.colorHead = token('--k4-fg', '#ffffff');
    this.colorTrail = token('--k4-ink-muted', '#8a8a8a');
    this.colorBg = token('--k4-bg', '#000000');

    if (this.dataset.k4Logo) {
      this.logo = new Image();
      this.logo.src = this.dataset.k4Logo;
    }

    this.resize = this.resize.bind(this);
    this.frame = this.frame.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    window.addEventListener('resize', this.resize);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.resize();
    if (this.reduced) {
      for (let i = 0; i < 40; i += 1) this.step();
    } else {
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    window.removeEventListener('resize', this.resize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  onVisibility() {
    if (this.reduced) return;
    if (document.hidden) this.stop();
    else this.start();
  }

  start() {
    if (this.raf) return;
    this.last = 0;
    this.raf = requestAnimationFrame(this.frame);
  }

  stop() {
    cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.clientWidth || window.innerWidth;
    const h = this.clientHeight || window.innerHeight;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = w;
    this.h = h;

    const cols = Math.ceil(w / this.fontPx);
    const kinds = ['word', 'chain', 'mix', 'word'];
    // Każda kolumna startuje w losowym miejscu, żeby deszcz nie szedł ścianą.
    this.cols = Array.from({ length: cols }, (_, i) => ({
      kind: kinds[(i + Math.floor(Math.random() * 2)) % kinds.length],
      y: Math.floor(Math.random() * (h / this.fontPx)) * -1,
      speed: 0.5 + Math.random() * 0.8,
      step: 0,
    }));

    this.ctx.fillStyle = this.colorBg;
    this.ctx.fillRect(0, 0, w, h);
    this.ctx.font = `${this.fontPx}px ui-monospace, Menlo, Consolas, monospace`;
    this.ctx.textBaseline = 'alphabetic';
  }

  frame(now) {
    // ~24 klatki/s wystarczą; mniej pracy GPU na telefonie.
    if (now - this.last > 42) {
      this.last = now;
      this.step();
    }
    this.raf = requestAnimationFrame(this.frame);
  }

  glyph(col) {
    const set = SETS[col.kind];
    if (col.kind === 'word') {
      col.step = (col.step + 1) % set.length;
      return set[col.step];
    }
    return set[Math.floor(Math.random() * set.length)];
  }

  step() {
    const { ctx, w, h, fontPx } = this;
    ctx.globalAlpha = 0.08;
    ctx.fillStyle = this.colorBg;
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;

    for (let i = 0; i < this.cols.length; i += 1) {
      const col = this.cols[i];
      const y = Math.floor(col.y) * fontPx;
      const x = i * fontPx;

      if (y > 0) {
        const useLogo = this.logo && this.logo.complete && this.logo.naturalWidth > 0 && Math.random() < 0.012;
        if (useLogo) {
          // Znak czarny na alfie → invert daje biel; rysowany w rozmiarze glifu.
          ctx.filter = 'invert(1)';
          ctx.drawImage(this.logo, x - 1, y - fontPx, fontPx + 2, fontPx + 2);
          ctx.filter = 'none';
        } else {
          const g = this.glyph(col);
          // Poprzedni znak w kolumnie gaśnie do szarości, nowy świeci bielą.
          ctx.fillStyle = this.colorTrail;
          ctx.fillText(g, x, y - fontPx);
          ctx.fillStyle = this.colorHead;
          ctx.fillText(g, x, y);
        }
      }

      col.y += col.speed;
      if (y > h && Math.random() > 0.975) {
        col.y = 0;
        col.speed = 0.5 + Math.random() * 0.8;
      }
    }
  }
}

if (!customElements.get('k4-rain')) customElements.define('k4-rain', K4Rain);
